import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/Logo';
import { Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Step 3 — user picks a new password. On success sessionStorage is cleared
 * and we route back to /login with a success toast so the user can sign in
 * with the new credentials.
 */
export default function ResetPasswordPage() {
  const nav = useNavigate();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const token = sessionStorage.getItem('32d.reset.token') || '';

  useEffect(() => {
    if (!token) nav('/forgot-password', { replace: true });
  }, [token, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw1.length < 8) return setError('Password must be at least 8 characters.');
    if (pw1 !== pw2) return setError('Passwords do not match.');
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { reset_token: token, new_password: pw1 });
      sessionStorage.removeItem('32d.reset.token');
      sessionStorage.removeItem('32d.reset.email');
      toast.success('Password reset — sign in with your new password.');
      nav('/login', { replace: true });
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r"
           style={{ background: 'hsl(var(--surface-1))', borderColor: 'hsl(var(--border))' }}>
        <Logo variant="horizontal" surface="light" height={44} className="max-w-[280px]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Choose a new password.
          </h1>
          <p className="mt-4 text-[hsl(var(--foreground-muted))] max-w-md text-[15px] leading-relaxed">
            Pick something you don&rsquo;t use elsewhere. Minimum 8 characters — a mix of letters,
            numbers, and a symbol is a good habit.
          </p>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-subtle))]">
          You&rsquo;ll be signed out on other devices after saving.
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="reset-password-form">
          <div className="lg:hidden flex justify-center mb-2">
            <Logo variant="stacked" surface="light" height={90} />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Lock size={22} />New password
            </h2>
            <p className="text-sm text-[hsl(var(--foreground-muted))]">
              Reset link is valid for 10 minutes.
            </p>
          </div>

          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">New password</span>
            <Input data-testid="reset-password-new"
                   required type="password" value={pw1}
                   onChange={e => setPw1(e.target.value)}
                   minLength={8} maxLength={128}
                   placeholder="At least 8 characters"
                   className="mt-1" autoComplete="new-password" />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Confirm password</span>
            <Input data-testid="reset-password-confirm"
                   required type="password" value={pw2}
                   onChange={e => setPw2(e.target.value)}
                   minLength={8} maxLength={128}
                   className="mt-1" autoComplete="new-password" />
          </label>

          {error && (
            <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border"
                 style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
                 role="alert" data-testid="reset-password-error">
              {error}
            </div>
          )}

          <Button data-testid="reset-password-submit"
                  disabled={busy}
                  type="submit" className="w-full">
            {busy ? 'Saving…' : 'Save new password'}
          </Button>

          <div className="text-[12.5px] text-center text-[hsl(var(--foreground-muted))]">
            <Link className="inline-flex items-center gap-1 underline" to="/login" data-testid="reset-password-back">
              <ArrowLeft size={13} />Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
